import React, { useRef, useState, useImperativeHandle, forwardRef, useEffect } from 'react';
import { View, Image, Text, StyleSheet, TouchableOpacity } from 'react-native';
import CashierProviders from '@kspay/oversea-cashier-provider-rn';
import CashierJssdk, { GOPAY_CALLBACK_EVENT_ENUMS } from '@kspay/oversea-cashier-jssdk';
import { VERSION, PKGNAME, RESULT_ENUMS } from "./common/constant";
import logger from "./common/logger";
import { lrpx } from "./common/utils";
import { getLanguage } from "./common/i18n";
import { safeIcon } from "./assets/base64Icons";
import Popup, { PopupLayout } from "../Popup";
import { langMap } from "./lang";
import { Platform } from 'react-native';
const DEFAULT_SHOW_ITEM_NUM = 30;
const styles = StyleSheet.create({
  safeContainer: {
    height: lrpx(40),
    flexDirection: 'row',
    justifyContent: 'flex-start',
    alignItems: 'center',
    backgroundColor: 'rgba(5, 163, 0, 0.08)',
    paddingLeft: lrpx(19)
  },
  safeIcon: {
    width: lrpx(16),
    height: lrpx(16)
  },
  safeText: {
    marginLeft: lrpx(8),
    fontSize: lrpx(14),
    lineHeight: lrpx(16),
    color: '#05A300'
  },
  buttonBox: {
    marginTop: lrpx(16),
    width: lrpx(414),
    alignItems: 'center'
  },
  buttonContainer: {
    height: lrpx(48),
    width: lrpx(366),
    backgroundColor: '#ff4906',
    borderRadius: lrpx(6),
    justifyContent: 'center',
    alignItems: 'center'
  },
  buttonTitle: {
    color: '#fff',
    fontSize: lrpx(18),
    lineHeight: lrpx(21),
    fontWeight: '700'
  }
});
const HalfScreenCashier = (props, ref) => {
  var _langMap$userLang, _langMap$userLang2, _cashierConfig$provid, _cashierConfig$provid2, _cashierConfig$provid3, _cashierConfig$provid4, _langMap$userLang3;
  const {
    yoda,
    envConfig = {},
    logConfig = {},
    uiConfig = {}
  } = props;
  const [popup, setPopup] = useState(false);
  const cashierProvidersRef = useRef(null);
  const [cashierConfig, setCashierConfig] = useState({});
  const [promiseHanlder, setPromiseHanlder] = useState({});
  const [cashierCallback, setCashierCallback] = useState(null);
  const userLang = getLanguage(envConfig.language);
  useEffect(() => {
    var _logConfig$enabled, _logConfig$debug;
    logger.init({
      enabled: (_logConfig$enabled = logConfig === null || logConfig === void 0 ? void 0 : logConfig.enabled) !== null && _logConfig$enabled !== void 0 ? _logConfig$enabled : true,
      debug: (_logConfig$debug = logConfig === null || logConfig === void 0 ? void 0 : logConfig.debug) !== null && _logConfig$debug !== void 0 ? _logConfig$debug : false,
      weblog: logConfig === null || logConfig === void 0 ? void 0 : logConfig.weblog,
      mode: 'radar',
      logNamespace: PKGNAME,
      logCategory: {
        sdkVersion: VERSION,
        ...(logConfig === null || logConfig === void 0 ? void 0 : logConfig.customLogParams)
      },
      radarConfig: {
        projectId: '96b6eb801t',
        whiteUrlList: []
      },
      radarKrnConfig: {
        eventType: 'CUSTOM',
        bundleId: 'overseaPaySdk'
      }
    });
    logger.info('initialization completed.');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useImperativeHandle(ref, () => ({
    show: (showCashierProps, callback) => {
      return new Promise((resolve, reject) => {
        var _showCashierProps$pay;
        // promise & callback 暂存，用于后续用户下单时结果反馈
        setPromiseHanlder({
          resolve,
          reject
        });
        setCashierCallback(() => callback);

        // trade_no 用于埋点溯源
        const trade_no = showCashierProps === null || showCashierProps === void 0 ? void 0 : (_showCashierProps$pay = showCashierProps.paymentData) === null || _showCashierProps$pay === void 0 ? void 0 : _showCashierProps$pay.trade_no;
        logger.debug('show triggered.', showCashierProps);
        logger.info('show triggered.', trade_no);
        if (!showCashierProps) return;

        // cashierConfig 存储收银台渠道与订单信息
        setCashierConfig(showCashierProps);
        setPopup(true);
      });
    }
  }));
  const getSelectedPaymentAndDoPay = async () => {
    var _cashierConfig$paymen, _cashierProvidersRef$, _cashierProvidersRef$2, _logConfig$customLogP, _params$paymentType, _params$paymentMethod;
    // trade_no 用于埋点溯源
    const trade_no = cashierConfig === null || cashierConfig === void 0 ? void 0 : (_cashierConfig$paymen = cashierConfig.paymentData) === null || _cashierConfig$paymen === void 0 ? void 0 : _cashierConfig$paymen.trade_no;

    // 获取收银台支付渠道信息
    const params = (_cashierProvidersRef$ = cashierProvidersRef.current) === null || _cashierProvidersRef$ === void 0 ? void 0 : (_cashierProvidersRef$2 = _cashierProvidersRef$.getCashierParams) === null || _cashierProvidersRef$2 === void 0 ? void 0 : _cashierProvidersRef$2.call(_cashierProvidersRef$);

    // 处理埋点参数
    const customLogParams = (_logConfig$customLogP = logConfig === null || logConfig === void 0 ? void 0 : logConfig.customLogParams) !== null && _logConfig$customLogP !== void 0 ? _logConfig$customLogP : {};
    customLogParams.payment_type = (_params$paymentType = params === null || params === void 0 ? void 0 : params.paymentType) !== null && _params$paymentType !== void 0 ? _params$paymentType : '';
    customLogParams.payment_method = (_params$paymentMethod = params === null || params === void 0 ? void 0 : params.paymentMethod) !== null && _params$paymentMethod !== void 0 ? _params$paymentMethod : '';
    customLogParams.payPage = 'CASHIER_RN_SDK';

    // 收银台SDK初始化
    const instance = new CashierJssdk({
      envConfig: envConfig,
      logConfig: Object.assign({}, logConfig, {
        customLogParams
      })
    });
    const delayTime = Platform.OS === 'ios' ? 200 : 0; // ios下必须先关闭弹窗再延迟跳转rn，否则会失败
    setPopup(false);
    setTimeout(async () => {
      try {
        var _params$paymentType2, _params$paymentMethod2;
        // 触发支付单下单
        const result = await instance.goPay({
          orderAmount: cashierConfig === null || cashierConfig === void 0 ? void 0 : cashierConfig.orderAmount,
          country: cashierConfig === null || cashierConfig === void 0 ? void 0 : cashierConfig.country,
          data: {
            ...(cashierConfig === null || cashierConfig === void 0 ? void 0 : cashierConfig.paymentData),
            payment_type: (_params$paymentType2 = params === null || params === void 0 ? void 0 : params.paymentType) !== null && _params$paymentType2 !== void 0 ? _params$paymentType2 : '',
            payment_method: (_params$paymentMethod2 = params === null || params === void 0 ? void 0 : params.paymentMethod) !== null && _params$paymentMethod2 !== void 0 ? _params$paymentMethod2 : ''
          },
          yoda: yoda,
          callback: ({
            event
          }) => {
            // 出现短验弹窗，收起收银台执行短验流程
            if (event === GOPAY_CALLBACK_EVENT_ENUMS.API_RETURN_NEED_SMS_VERIFY) {
              setPopup(false);
            }
          }
        });
        // 支付单下单成功
        logger.info('getSelectedPaymentAndDoPay success', trade_no, result);

        // promise & callback 结果透出给业务方
        promiseHanlder === null || promiseHanlder === void 0 ? void 0 : promiseHanlder.resolve(result);
        cashierCallback && cashierCallback(result);
      } catch (err) {
        // 支付单下单失败，关闭弹窗
        setPopup(false);
        logger.error('getSelectedPaymentAndDoPay error', trade_no, err);

        // promise & callback 结果透出给业务方
        promiseHanlder === null || promiseHanlder === void 0 ? void 0 : promiseHanlder.reject(err);
        cashierCallback && cashierCallback(err);
      }
    }, delayTime);
  };
  return /*#__PURE__*/React.createElement(Popup, {
    visible: popup,
    onClose: () => {
      setPopup(false);
      const result = {
        result: RESULT_ENUMS.USER_CANCEL_HALFSCREEN_CASHIER
      };
      promiseHanlder === null || promiseHanlder === void 0 ? void 0 : promiseHanlder.reject(result);
      cashierCallback && cashierCallback(result);
    },
    position: "bottom",
    style: {
      zIndex: 1,
      height: uiConfig.height && lrpx(uiConfig.height)
    }
  }, /*#__PURE__*/React.createElement(PopupLayout, {
    title: cashierConfig.orderAmount,
    cTitle: (_langMap$userLang = langMap[userLang]) === null || _langMap$userLang === void 0 ? void 0 : _langMap$userLang.Amount
  }, /*#__PURE__*/React.createElement(View, null, /*#__PURE__*/React.createElement(View, {
    style: styles.safeContainer
  }, /*#__PURE__*/React.createElement(Image, {
    style: styles.safeIcon,
    source: {
      uri: safeIcon
    }
  }), /*#__PURE__*/React.createElement(Text, {
    style: styles.safeText
  }, (_langMap$userLang2 = langMap[userLang]) === null || _langMap$userLang2 === void 0 ? void 0 : _langMap$userLang2.PayTip)), /*#__PURE__*/React.createElement(CashierProviders, {
    ref: cashierProvidersRef,
    language: envConfig.language,
    cashierConfigs: (_cashierConfig$provid = cashierConfig.providerData) === null || _cashierConfig$provid === void 0 ? void 0 : _cashierConfig$provid.cashierConfigs,
    showItemNum: ((_cashierConfig$provid2 = cashierConfig.providerData) === null || _cashierConfig$provid2 === void 0 ? void 0 : _cashierConfig$provid2.showItemNum) || DEFAULT_SHOW_ITEM_NUM,
    selectedPayMethod: (_cashierConfig$provid3 = cashierConfig.providerData) === null || _cashierConfig$provid3 === void 0 ? void 0 : _cashierConfig$provid3.selectedPayMethod,
    callback: (_cashierConfig$provid4 = cashierConfig.providerData) === null || _cashierConfig$provid4 === void 0 ? void 0 : _cashierConfig$provid4.callback
  }), /*#__PURE__*/React.createElement(View, {
    style: styles.buttonBox
  }, /*#__PURE__*/React.createElement(TouchableOpacity, {
    onPress: getSelectedPaymentAndDoPay
  }, /*#__PURE__*/React.createElement(View, {
    style: styles.buttonContainer
  }, /*#__PURE__*/React.createElement(Text, {
    style: styles.buttonTitle
  }, (_langMap$userLang3 = langMap[userLang]) === null || _langMap$userLang3 === void 0 ? void 0 : _langMap$userLang3.Confirm)))))));
};
export default /*#__PURE__*/forwardRef(HalfScreenCashier);